import { computed, inject, onBeforeUnmount, type ComputedRef, type InjectionKey } from 'vue';

/**
 * What a Field hands to the controls inside it so its label is bound without any
 * consumer wiring: the generated id, and a claim on it. Only ONE control may hold
 * the claim at a time, so a Field with two controls never yields a duplicate id.
 */
export interface FieldContext {
  claim(owner: symbol): string | undefined;
  release(owner: symbol): void;
}

export const FIELD_CONTEXT_KEY: InjectionKey<FieldContext> = Symbol('lumi-field');

/**
 * The id a lib control puts on its native element: the one the consumer passed, else
 * the enclosing Field's (if this control is the first to ask), else none.
 */
export function useFieldControlId(explicit: () => string | undefined): ComputedRef<string | undefined> {
  const field = inject(FIELD_CONTEXT_KEY, null);
  const owner = Symbol('lumi-field-control');
  const claimed = field && !explicit() ? field.claim(owner) : undefined;
  if (field) onBeforeUnmount(() => field.release(owner));
  return computed(() => explicit() || claimed);
}
